import React from "react";
import { Box, Typography, Button } from "@mui/material";
import {
    LocalShipping as ShippingIcon,
    InboxOutlined as EmptyIcon,
    Refresh as RefreshIcon,
    SentimentDissatisfied as SadIcon,
} from "@mui/icons-material";
import { brandTokens } from "../theme";

// ─── Empty State ──────────────────────────────────────────────────────────────
interface EmptyStateProps {
    icon?: React.ReactNode;
    title: string;
    description?: string;
    actionLabel?: string;
    onAction?: () => void;
}

export function EmptyState({ icon, title, description, actionLabel, onAction }: EmptyStateProps) {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                px: 3,
                textAlign: "center",
            }}
        >
            <Box
                sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#F1F5F9",
                    mb: 3,
                    border: "2px dashed #CBD5E1",
                }}
            >
                {icon || <EmptyIcon sx={{ fontSize: 36, color: brandTokens.textMuted }} />}
            </Box>
            <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: brandTokens.textPrimary, mb: 0.5, letterSpacing: "-0.01em" }}
            >
                {title}
            </Typography>
            {description && (
                <Typography
                    variant="body2"
                    sx={{ color: brandTokens.textSecondary, maxWidth: 320, lineHeight: 1.6 }}
                >
                    {description}
                </Typography>
            )}
            {actionLabel && onAction && (
                <Button
                    variant="contained"
                    size="small"
                    onClick={onAction}
                    startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                    sx={{
                        mt: 3,
                        bgcolor: brandTokens.navy,
                        fontWeight: 600,
                        borderRadius: "8px",
                        px: 3,
                        "&:hover": { bgcolor: brandTokens.navyLight },
                    }}
                >
                    {actionLabel}
                </Button>
            )}
        </Box>
    );
}

// ─── Error State ──────────────────────────────────────────────────────────────
interface ErrorStateProps {
    title?: string;
    message?: string;
    onRetry?: () => void;
}

export function ErrorState({
    title = "Something went wrong",
    message = "We couldn't load the data. Please check your connection and try again.",
    onRetry,
}: ErrorStateProps) {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                px: 3,
                textAlign: "center",
            }}
        >
            <Box
                sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #FEE2E2, #FECACA)",
                    mb: 3,
                }}
            >
                <SadIcon sx={{ fontSize: 36, color: "#DC2626" }} />
            </Box>
            <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: brandTokens.textPrimary, mb: 0.5, letterSpacing: "-0.01em" }}
            >
                {title}
            </Typography>
            <Typography
                variant="body2"
                sx={{ color: brandTokens.textSecondary, maxWidth: 340, lineHeight: 1.6 }}
            >
                {message}
            </Typography>
            {onRetry && (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={onRetry}
                    startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                    sx={{
                        mt: 3,
                        borderColor: "#DC2626",
                        color: "#DC2626",
                        fontWeight: 600,
                        borderRadius: "8px",
                        px: 3,
                        "&:hover": { bgcolor: "#FEF2F2", borderColor: "#B91C1C" },
                    }}
                >
                    Try Again
                </Button>
            )}
        </Box>
    );
}

// ─── No Shipments (driver-specific) ──────────────────────────────────────────
export function NoShipments() {
    return (
        <EmptyState
            icon={<ShippingIcon sx={{ fontSize: 36, color: brandTokens.textMuted }} />}
            title="No shipments yet"
            description="You don't have any shipments assigned to you right now. Hang tight — your dispatcher will assign loads soon."
        />
    );
}
